import React, { Suspense, useEffect, useRef } from "react";
import Typewriter from "typewriter-effect";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, MeshDistortMaterial, Sphere, Sparkles, Stars } from "@react-three/drei";
import "./Hero.css";

const stats = [
  { num: "2+", label: "Years Experience" },
  { num: "10K+", label: "Messages / Sec" },
  { num: "15+", label: "Substations Live" },
  { num: "99.9%", label: "System Uptime" },
];

const stack = ["React 19", "TypeScript", "Django", "FastAPI", "PostgreSQL", "Kafka", "GenAI"];

const bubbleConfig = [
  { size: 0.2, pos: [0.3, 0.3, 0.15], color: "#c4b5fd", speed: 1.45 },
  { size: 0.15, pos: [1.2, 0.7, -0.45], color: "#ddd6fe", speed: 1.25 },
  { size: 0.14, pos: [-1.25, 0.8, -0.55], color: "#a5b4fc", speed: 1.35 },
  { size: 0.17, pos: [-0.9, -0.95, 0.05], color: "#c4b5fd", speed: 1.15 },
  { size: 0.13, pos: [0.95, -1.15, -0.55], color: "#bfdbfe", speed: 1.3 },
  { size: 0.1, pos: [0.05, 1.25, -0.75], color: "#ddd6fe", speed: 1.28 },
  { size: 0.09, pos: [-0.2, 0.05, -0.95], color: "#c4b5fd", speed: 1.1 },
  { size: 0.08, pos: [0.55, -0.35, -1.05], color: "#a5b4fc", speed: 1.2 },
];

function BubbleCluster() {
  const groupRef = useRef();

  useFrame((state) => {
    if (!groupRef.current) return;
    groupRef.current.rotation.y = state.clock.elapsedTime * 0.08;
    groupRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.22) * 0.06;
  });

  return (
    <group ref={groupRef}>
      <Float speed={1.5} rotationIntensity={0.15} floatIntensity={0.65}>
        <Sphere args={[0.42, 48, 48]} position={[0, 0, 0]}>
          <MeshDistortMaterial
            color="#c4b5fd"
            roughness={0.14}
            metalness={0.55}
            distort={0.15}
            speed={1.2}
            transparent
            opacity={0.45}
          />
        </Sphere>
      </Float>

      {bubbleConfig.map((bubble, index) => (
        <Float
          key={index}
          speed={bubble.speed}
          rotationIntensity={0.35}
          floatIntensity={1.4}
        >
          <Sphere args={[bubble.size, 36, 36]} position={bubble.pos}>
            <meshPhysicalMaterial
              color={bubble.color}
              transparent
              opacity={0.34}
              roughness={0.02}
              metalness={0.15}
              clearcoat={1}
              clearcoatRoughness={0.05}
            />
          </Sphere>
        </Float>
      ))}
    </group>
  );
}

function HeroScene() {
  return (
    <Canvas camera={{ position: [0, 0, 6.7], fov: 45 }} dpr={[1, 1.6]}>
      <color attach="background" args={["#070b10"]} />
      <fog attach="fog" args={["#070b10", 7, 14]} />
      <ambientLight intensity={0.65} />
      <pointLight position={[2.8, 2.6, 4]} color="#c4b5fd" intensity={26} distance={12} />
      <pointLight position={[-2.8, -2, 3]} color="#d97757" intensity={18} distance={11} />

      <Suspense fallback={null}>
        <Stars radius={40} depth={30} count={1600} factor={2.2} saturation={0.4} fade speed={0.35} />
        <Sparkles count={54} size={2.2} scale={[6.2, 4.6, 6]} speed={0.28} color="#ddd6fe" />
        <BubbleCluster />
      </Suspense>
    </Canvas>
  );
}

function Hero() {
  const heroRef = useRef(null);

  useEffect(() => {
    const blob = document.getElementById("hero-blob");
    const onMove = (e) => {
      if (!blob) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 24;
      const y = (e.clientY / window.innerHeight - 0.5) * 24;
      blob.style.transform = `translate(${x}px, ${y}px)`;
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <section id="hero" className="hero" ref={heroRef}>
      <div className="hero-canvas-wrap" aria-hidden="true">
        <HeroScene />
      </div>

      <div className="hero-blob" id="hero-blob" />
      <div className="hero-blob hero-blob--amber" />

      <div className="hero-inner">
        <div className="hero-content">
          <div className="hero-status">
            <span className="hero-status__dot" />
            Open to full-time & remote roles
          </div>

          <h1 className="hero-name">
            Kashish
            <span className="hero-name__gradient"> Shukla</span>
          </h1>

          <p className="hero-tagline">React Developer · Django · FastAPI · PostgreSQL · GenAI</p>

          <div className="hero-role">
            <Typewriter
              options={{
                strings: [
                  "Real-Time Dashboard Engineer",
                  "React 19 + TypeScript Specialist",
                  "Django & FastAPI Backend Builder",
                  "WebSocket & SSE Live Systems",
                  "2+ Years · IIT Kanpur",
                ],
                autoStart: true,
                loop: true,
                deleteSpeed: 45,
                delay: 70,
              }}
            />
          </div>

          <p className="hero-bio card-body reveal">
            React Developer with <strong>2+ years</strong> building real-time dashboards
            and scalable web apps using <strong>React, TypeScript, Django, FastAPI,</strong> and{" "}
            <strong>PostgreSQL</strong>. Delivering production systems at IIT Kanpur for
            power grid monitoring and utility operations.
          </p>

          <div className="hero-stack tag-row">
            {stack.map((tech) => (
              <span key={tech} className="tag">{tech}</span>
            ))}
          </div>

          <div className="hero-ctas">
            <a href="#projects" className="btn-primary">
              View Projects
            </a>
            <a href="#contact" className="btn-outline">
              Get In Touch
            </a>
          </div>
        </div>

        <aside className="hero-panel" aria-label="Career highlights">
          <div className="hero-panel__card ui-card ui-card--flat">
            <p className="section-label">Currently</p>
            <p className="card-title">Full Stack Developer</p>
            <p className="hero-panel__org">IIT Kanpur · Project Associate</p>
          </div>

          <div className="hero-stats">
            {stats.map(({ num, label }) => (
              <div className="stat ui-card ui-card--flat" key={label}>
                <span className="stat__num">{num}</span>
                <span className="stat__label">{label}</span>
              </div>
            ))}
          </div>
        </aside>
      </div>

      <a href="#about" className="hero-scroll" aria-label="Scroll to about">
        <span className="hero-scroll__line" />
        Scroll
      </a>
    </section>
  );
}

export default Hero;
